/** A month-by-month series that is not the whole workspace's - a site's, or one scope on one site -
 * drawn by the one chart every band uses. Design review 2026-11-03 (dataviz: "one series, two chart
 * systems, two honesty rules"; "By month: no chart on the one tab whose whole content is a series").
 *
 * The site page drew its months with the older `MonthlyChart`: every month solid, a note in kilograms,
 * its own ticks. Home, the scope pages, the Executive dashboard and the Emissions screen draw the same
 * series with `MonthlyCard`, where what is still arriving is pale **per scope**, an estimate is said in
 * tonnes and a month opens its figures. A reader who saw both was told the same year two ways.
 *
 * **What is the narrowed series' own.** The series is `GET /inventory/monthly` (with `facility_id` for a
 * site): its months, summed by the server. The gaps are the expected sources of that site only (the
 * census narrowed to it), so a bill missing at another site never paints this site's bars pale. When
 * the caller knows the total the months must add up to, they are checked against it before anything is
 * drawn (`reconcile`), exactly as the workspace chart refuses to draw a series that does not add up. A
 * scope the server did not assess has no series (Scope 3 is not attributed to a site).
 *
 * `data-testid="inventory-monthly"` and its `data-state` are kept: they are the states the browser gate
 * reads (`loaded`, `empty`, never `unreconciled` or `failed` on the seeded workspace).
 *
 * **Two frames.** `card` is a `MonthlyCard` (a page that has a band); `bare` is the chart alone, for the
 * `TablePage` chart slot, which already is a card (a card inside a card draws two borders).
 */

import type { InventoryMonthly } from "../../api/queries";
import type { Coverage } from "../../api/sources";
import { MonthlyBars } from "../../components/charts";
import { EmptyState } from "../../components/ui/EmptyState";
import { SkeletonRows } from "../../components/ui/Skeleton";
import { modelOf, reconcile } from "../monthlyModel";
import { BandFailures } from "./BandFailures";
import { MonthlyCard } from "./MonthlyCard";
import { barMonths, estimateNote, scopeGaps } from "./bandModel";
import type { ScopeKey } from "./bandModel";

interface Read<T> {
  data: T | undefined;
  error: unknown;
  isPending: boolean;
  refetch: () => unknown;
}

/** The scopes the series assessed in any of its months, in stacking order. Read from the months themselves
 * (the cells the bars are drawn from), so a scope with a bar is a scope the series states and a scope the
 * server did not assess for the narrowed series has no bar (Scope 3 is not attributed to a site). */
function scopesOf(series: InventoryMonthly): ScopeKey[] {
  return (["1", "2", "3"] as const).filter((scope) =>
    series.months.some((month) =>
      month.by_scope.some((cell) => String(cell.scope) === scope && cell.assessed),
    ),
  );
}

const SERIES = {
  "1": { key: "1", label: "Scope 1", colour: "scope1" },
  "2": { key: "2", label: "Scope 2", colour: "scope2" },
  "3": { key: "3", label: "Scope 3", colour: "scope3" },
} as const;

export function SeriesMonthly({
  read,
  census,
  facilityId,
  scopes: only,
  expectedKg,
  onOpenMonth,
  frame = "card",
  caption = "Emissions by month",
}: {
  read: Read<InventoryMonthly>;
  census: Read<Coverage>;
  /** The site the series is for; the census is narrowed to its sources. Absent: the whole workspace. */
  facilityId?: string;
  /** Draw only these scopes (a scope page's one scope). Absent: every scope the series has a figure in. */
  scopes?: readonly ScopeKey[];
  /** The total the months must add up to, when the caller states one (a site's entry in the inventory). */
  expectedKg?: string | undefined;
  /** Open the figures of a month (`YYYY-MM`) with its dates. Absent: the bars are not a way in. */
  onOpenMonth?: (month: string, dates: { start: string; end: string }) => void;
  frame?: "card" | "bare";
  caption?: string;
}) {
  const series = read.data;
  const model = series ? modelOf(series, expectedKg) : undefined;
  const scopes = only ?? (series ? scopesOf(series) : []);
  const mine: Coverage | undefined = census.data
    ? {
        ...census.data,
        sources: facilityId
          ? census.data.sources.filter((source) => source.facility_id === facilityId)
          : census.data.sources,
      }
    : undefined;
  const check = series ? reconcile(series, expectedKg) : undefined;
  const state = read.error
    ? "failed"
    : read.isPending || !model
      ? "loading"
      : model.months.length === 0 || model.recordedCount === 0
        ? "empty"
        : !model.reconciliation.ok
          ? "unreconciled"
          : "loaded";
  const drawable = series !== undefined && mine !== undefined && check?.ok === true && scopes.length > 0;
  const bars = drawable ? barMonths(series.months, scopes, scopeGaps(mine, scopes)) : undefined;
  const estimates = series && scopes.length > 1 ? estimateNote(series) : undefined;
  const open = onOpenMonth
    ? (key: string) => {
        const hit = series?.months.find((entry) => entry.month === key);
        if (hit) onOpenMonth(key, { start: hit.bucket_start, end: hit.bucket_end });
      }
    : undefined;
  const unreconciled = check && !check.ok ? (check.reason ?? "The months do not add up.") : undefined;

  return (
    <div data-testid="inventory-monthly" data-state={state}>
      {/* A failed read says it failed, with its retry, and draws nothing: a year with no bars is a claim
          about the company or the site. */}
      <BandFailures
        reads={[
          { of: "The monthly figures", read },
          { of: "The expected bills", read: census },
        ]}
      />
      {/* Still out comes first: an "empty" drawn before the census has answered is the answered-empty screen
          for a read nobody has seen (`screenHolds`). A card draws the grey rows itself (`BandCard`). */}
      {frame === "bare" && !read.error && !census.error && (read.isPending || census.isPending) ? (
        <SkeletonRows rows={3} />
      ) : state === "empty" && !census.isPending ? (
        <EmptyState title="No figure in this window" compact testId="series-monthly-empty" />
      ) : frame === "card" ? (
        <MonthlyCard
          scopes={scopes}
          bars={bars}
          reads={[read, census]}
          {...(open ? { onOpenMonth: open } : {})}
          testId="series-monthly-card"
          {...(unreconciled ? { unreconciled } : {})}
          {...(estimates ? { estimates } : {})}
        />
      ) : unreconciled ? (
        <p role="alert" data-testid="band-monthly-unreconciled">
          The monthly series is not shown because it does not add up to the window total ({unreconciled}).
        </p>
      ) : bars ? (
        <MonthlyBars
          months={bars}
          series={scopes.map((scope) => SERIES[scope])}
          unit="tCO2e"
          caption={caption}
          height={180}
          testId="band-monthly"
          {...(estimates ? { legendNote: estimates } : {})}
          {...(open ? { onOpenReceipt: (key: string) => open(key) } : {})}
        />
      ) : null}
    </div>
  );
}
